<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  ContractGenerationTemplateQuestionInDb,
  ContractGenerationTemplateQuestionsResponse,
  ContractGenerationVariantAnswersSummary,
} from '@/api';
import { TemplateQuestionsService } from '@/services/contractGeneration/templateQuestionsService';
import {
  answerDraftError,
  answerDraftFor,
  blankAnswerDraft,
  isAnswerDraftFilled,
  toVariantAnswerPayload,
  type VariantAnswerDraftMap,
} from '@/utils/contractGeneration/questions';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import VariantConstraintFields from './VariantConstraintFields.vue';

/**
 * What one document covers — its answers, which are constraints rather than
 * values: one option, or a half-open band of numbers or dates.
 *
 * The write replaces the whole set rather than merging, which is why the form
 * opens on what the document covers today and sends all of it back.
 */
interface Props {
  show: boolean;
  templateUid: string;
  variant: ContractGenerationVariantAnswersSummary;
  questions: ContractGenerationTemplateQuestionInDb[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  /** The whole questions state, as the write answered with it. */
  (e: 'saved', value: ContractGenerationTemplateQuestionsResponse): void;
}>();

const message = useGaiaToast();
const saving = ref<boolean>(false);
const draft = ref<VariantAnswerDraftMap>({});

watch(
  () => [props.show, props.variant.variant_uid] as const,
  ([show]) => {
    if (show) draft.value = answerDraftFor(props.variant, props.questions);
  },
  { immediate: true }
);

const hasError = computed<boolean>(() =>
  props.questions.some(
    question => answerDraftError(question, draft.value[question.uid] ?? blankAnswerDraft()) !== null
  )
);

/** Required questions this document would still leave unanswered. */
const missingRequired = computed<ContractGenerationTemplateQuestionInDb[]>(() =>
  props.questions.filter(
    question =>
      question.required_for_variants &&
      !isAnswerDraftFilled(question, draft.value[question.uid] ?? blankAnswerDraft())
  )
);

async function save(): Promise<void> {
  if (hasError.value || saving.value) return;
  saving.value = true;
  try {
    const state = await TemplateQuestionsService.setVariantAnswers(
      props.templateUid,
      props.variant.variant_uid,
      toVariantAnswerPayload(props.questions, draft.value)
    );
    emit('saved', state);
    emit('update:show', false);
    message.success(`Answers saved for “${props.variant.variant_name}”.`);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save the answers.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="520px"
    :mask-closable="!saving"
    :closable="!saving"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <n-flex vertical :size="6">
        <gaia-text size="subtitle" weight="semibold" tone="ink">Answers</gaia-text>
        <gaia-text size="row" tone="text">
          What “{{ props.variant.variant_name }}” covers. Leave a question blank and it won’t narrow
          on that one.
        </gaia-text>
      </n-flex>
    </template>

    <n-flex vertical :size="16" class="answers-body">
      <variant-constraint-fields
        :questions="props.questions"
        :draft="draft"
        :disabled="saving"
        @update:draft="(value: VariantAnswerDraftMap) => (draft = value)"
      />

      <gaia-banner v-if="missingRequired.length > 0" type="warning">
        “{{ missingRequired.map(question => question.label).join('”, “') }}” must be answered by
        every document. Until this one does, the whole template can’t be drafted from.
      </gaia-banner>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="saving" @click="emit('update:show', false)">
          Cancel
        </gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="saving"
          :disabled="hasError"
          @click="save"
        >
          Save answers
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* A template with several questions makes this dialog taller than the screen.
   The body scrolls on its own so the footer — and the buttons that close it —
   stay reachable. Capped against the viewport rather than a fixed height, so a
   short form still sizes to its content. */
.answers-body {
  max-height: min(62vh, 560px);
  overflow-y: auto;
  /* Room for the focus ring on the last field, which the scroll box clips. */
  padding: 2px 2px 4px;
  margin: -2px -2px -4px;
}
</style>
